import React, { useEffect, useRef, useState } from "react";
import { useOutletContext, useLocation, Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Wrench,
    ShieldCheck,
    Database,
    Network,
    Globe,
    ShieldAlert,
    Lock,
    KeyRound,
    FileSearch,
    ClipboardCheck,
    ArrowRight,
    ArrowUp,
    Check,
    Sparkles,
    Loader2,
    MessageSquare,
    Wand2,
    Mail,
    CalendarCheck,
    Rocket,
    Hammer,
    Radar,
} from "lucide-react";
import { Chat } from "../components/Chat";
import { Reveal, RevealItem } from "../components/Reveal";
import { SpotlightCursor } from "../components/SpotlightCursor";
import { SEO, SITE_DOMAIN } from "../components/SEO";
import { WaitlistButton } from "../components/WaitlistButton";
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from "../components/ui/accordion";

// Waitlist mode flag — replaced at build time by CRA.
const WAITLIST_MODE =
    String(process.env.REACT_APP_WAITLIST_MODE || "").toLowerCase() === "true";

// =============================================================================
// Static content blocks (also reflected in JSON-LD where applicable)
// =============================================================================

const PILLARS = [
    {
        icon: Wrench,
        title: "Free Network Tools",
        body: "Seven utilities for daily ops — subnet calculator, DNS health, CVE lookup, TLS inspector, security headers, password breach check, NIS2 quiz. No signup. No ads. Bookmark-worthy.",
        cta: { label: "Browse all tools", to: "/tools" },
    },
    {
        icon: ShieldCheck,
        title: "Multi-Vendor Firewall Configurator",
        body: "Generate production-ready CLI and XML configs for Fortinet, WatchGuard, Zyxel, pfSense, and a generic CLI fallback. 37 hardware models, 7-step wizard, live preview.",
        cta: { label: "See the configurator", to: "/zeroconf" },
    },
    {
        icon: Database,
        title: "Lightweight IPAM & Hardware Inventory",
        body: "Track clients, subnets, hardware, and important IPs in one multi-tenant workspace. CPE-matched hardware ready for automated CVE alerts. CSV export included.",
        cta: { label: "Explore IPAM", to: "/ipam" },
    },
];

const FREE_TOOLS = [
    {
        to: "/tools/public-audit",
        icon: Radar,
        title: "Public Surface Audit",
        body: "Discover subdomains, certificates, and exposed services from public sources. Built-in risk score.",
        hint: "NEW · Free 1 scan / day · CT-log powered",
        featured: true,
    },
    {
        to: "/tools/subnet-calculator",
        icon: Network,
        title: "Subnet Calculator",
        body: "Convert CIDR to host ranges, broadcast, and netmask. IPv4 ready, IPv6 coming.",
        hint: "49,500 engineers search 'subnet calculator' monthly",
    },
    {
        to: "/tools/dns-health",
        icon: Globe,
        title: "DNS Health Check",
        body: "Audit A, MX, SPF, DMARC, DKIM, NS records for any domain.",
        hint: "33,100 engineers search 'dns lookup' monthly",
    },
    {
        to: "/tools/cve-lookup",
        icon: ShieldAlert,
        title: "CVE Lookup",
        body: "Search vulnerabilities by CVE ID or vendor against the NIST NVD.",
        hint: "Real-time CVSS scores and patch references",
    },
    {
        to: "/tools/tls-inspector",
        icon: Lock,
        title: "TLS/SSL Inspector",
        body: "Validate certificate chains, cipher suites, expiry, and protocol support.",
        hint: "8,000+ engineers verify SSL monthly",
    },
    {
        to: "/tools/security-headers",
        icon: ShieldCheck,
        title: "Security Headers",
        body: "Audit HSTS, CSP, X-Frame-Options, and Permissions-Policy headers.",
        hint: "Get an A+ grade for your hardening",
    },
    {
        to: "/tools/password-breach",
        icon: KeyRound,
        title: "Password Breach Check",
        body: "K-anonymity check against HaveIBeenPwned. Your password never leaves the browser.",
        hint: "100% client-side. Zero data exposure.",
    },
    {
        to: "/tools/nis2-assessment",
        icon: FileSearch,
        title: "NIS2 Compliance Quiz",
        body: "25-question self-assessment with weighted score and downloadable PDF.",
        hint: "Required reading for any EU-connected SMB",
    },
];

const ROADMAP = [
    { state: "live", label: "LIVE", text: "Free network tools (7 utilities)" },
    { state: "live", label: "LIVE", text: "Multi-vendor firewall configurator (5 vendors, 37 models)" },
    { state: "live", label: "LIVE", text: "Client Data Suite & lightweight IPAM" },
    { state: "active", label: "LIVE", text: "AI co-pilot function calling" },
    { state: "active", label: "ACTIVE", text: "Continuous CVE scanning vs hardware inventory (Q1 2026)" },
    { state: "planned", label: "PLANNED", text: "Multi-framework compliance: NIST CSF 2.0, CMMC, SOC 2, HIPAA (Q2 2026)" },
    { state: "planned", label: "PLANNED", text: "Team/Business tier with SSO/SAML, audit logs, RBAC (Q2 2026)" },
    { state: "planned", label: "PLANNED", text: "Community Discord (when we hit 100 active users)" },
];

const VALUES = [
    "Transparent pricing — no \"contact sales\"",
    "No demo calls required",
    "Cancel anytime, no annual lock-in",
    "Browser-side tools (your data, your machine)",
    "GDPR & CCPA compliant",
    "Honest about what we don't do",
];

const COMPARISON = {
    headers: ["Capability", "Cybersecurity Forefront", "Auvik", "NetBox", "ManageEngine OpManager"],
    rows: [
        ["Starting price", "$19.90/mo", "~$15/device/mo (opaque)", "Free (self-hosted) or $9.5/device/mo", "$95/year (10 devices)"],
        ["Free tier", "7 tools + AI chat (no signup)", "None", "Self-hosted free, Cloud 100 devices", "3 devices"],
        ["Multi-vendor firewall configurator", "✅ 5 vendors", "❌", "❌", "❌"],
        ["Lightweight IPAM", "✅", "Partial (topology)", "✅ (heavy)", "❌"],
        ["AI chat co-pilot", "✅", "❌", "❌", "❌"],
        ["Self-serve signup", "✅ 60 seconds", "Demo call required", "Self-host or sales", "Free trial"],
        ["SSO / SAML", "Roadmap (Team tier)", "✅", "$7,500/yr", "✅"],
        ["Real-time monitoring (SNMP)", "❌", "✅", "❌", "✅"],
        ["Target audience", "SMB IT, freelancers, growing MSPs", "Established MSPs", "Engineers / DevOps", "Enterprise IT"],
    ],
};

const PRICING_TEASER = [
    {
        key: "free",
        name: "Free",
        price: "$0",
        cadence: "forever",
        features: ["7 free network tools", "AI chat co-pilot (limited)", "NIS2 quiz + PDF report"],
        cta: { label: "Start free", to: "/signup", variant: "ghost" },
    },
    {
        key: "pro_monthly",
        name: "Pro",
        price: "$19.90",
        cadence: "per month",
        popular: true,
        features: [
            "Multi-vendor firewall configurator",
            "Client Data Suite (10 clients · 15 for Founding Members)",
            "Hardware inventory with CPE",
            "Important IPs tracking",
            "Save configs to clients",
            "CSV export",
        ],
        cta: { label: "Get notified", to: "/pricing", variant: "primary" },
    },
    {
        key: "msp",
        name: "MSP",
        price: "$199",
        cadence: "per year",
        features: ["Unlimited clients", "Priority support", "Early access to new features"],
        cta: { label: "Contact us", to: "/contact", variant: "ghost" },
    },
];

const FAQ_ITEMS = [
    {
        q: "What is Cybersecurity Forefront?",
        a: "Cybersecurity Forefront is a chat-first firewall configurator and Client IPAM for MSPs and in-house security teams, plus a suite of free compliance and network tools. We're a US LLC (Wyoming) operated by senior network and security engineers.",
    },
    {
        q: "What is the chat-first firewall configurator?",
        a: "Our flagship product (codename ZeroConf ZTP). You describe the deployment — hardware, WAN, VLANs, policies — and the wizard generates a reviewable, diff-able config you can ship to a Fortinet, WatchGuard, Zyxel, or pfSense device.",
    },
    {
        q: "Which firewall vendors do you support?",
        a: "Fortinet (FortiGate), WatchGuard (Firebox), Zyxel (USG / ATP), and pfSense. We add vendors based on customer demand — Cisco ASA, Juniper SRX, and SonicWall are on the shortlist.",
    },
    {
        q: "What is included in the free plan?",
        a: "The concierge AI chat, the read-only ZeroConf demo, and the full free-tools suite: subnet calculator, DNS health, TLS inspector, security headers, password breach lookup, CVE lookup, and NIS2 assessment. No email gate, no quota gimmicks.",
    },
    {
        q: "How does the Client IPAM work?",
        a: "One workspace per client. Each client holds subnets (IPv4 + IPv6), hardware inventory, important IPs and credentials, and a history of generated firewall configs. Archived clients are kept for audit. Hard-deletion is on the user, not us.",
    },
    {
        q: "Do you support NIS2 compliance?",
        a: "Yes. The NIS2 Assessment is a free 25-question self-assessment with scoring, evidence list, and a downloadable PDF. A full NIS2 Isolation Planner that maps your IPAM into compliant segmentation zones is on the Q3 2026 roadmap.",
    },
    {
        q: "Where is my data stored?",
        a: "MongoDB Atlas in a US region, encrypted in transit (TLS 1.2+) and at rest. Passwords are bcrypt-hashed. We do not send your stored customer content (IPAM, configs, credentials) to model providers for training.",
    },
    {
        q: "How is pricing structured?",
        a: "Free forever for the concierge chat and the seven free tools. Pro at $19.90 per month for the full ZeroConf wizard, saved projects, and config history. MSP at $199 per year for unlimited tenants and priority support.",
    },
    {
        q: "Do you offer support?",
        a: "Pro: email support with one-business-day response. MSP: priority support with a four-hour response SLA. Free plan: best-effort community support. All plans get product-update emails and a public changelog.",
    },
    {
        q: "How do I get started?",
        a: "Open the concierge chat at the top of this page and ask anything — services, pricing, ZeroConf — or click any free tool to use it without an account. To save projects or run the full wizard, create a free account and upgrade when you're ready.",
    },
    {
        q: "How does the AI co-pilot work?",
        a: "The AI chat on the homepage uses GPT-4o-mini to understand plain English questions about networking. It picks the right tool, invokes it directly, and returns formatted results in the conversation — subnet maths, DNS audits, CVE lookups, and (for Pro accounts) live IPAM queries against your own hardware and CVE alerts. Network engineering at conversation speed.",
    },
    {
        q: "Can I use this for my MSP managing multiple clients?",
        a: "Yes. The Client Data Suite is multi-tenant — each client gets isolated data, you switch context via dashboard. Pro tier supports up to 10 clients (15 for Founding Members), MSP tier is unlimited. Global search lets you find any subnet, asset, or IP across all your clients in one keystroke.",
    },
    {
        q: "What firewall vendors do you support in detail?",
        a: "Today: Fortinet FortiGate (CLI + XML), WatchGuard Firebox (CLI), Zyxel USG/ATP (CLI), Netgate pfSense (XML import), and a generic CLI fallback. 37 hardware models pre-cataloged. We add vendors based on user demand — Cisco ASA, Juniper SRX, and SonicWall are next.",
    },
    {
        q: "How do you compare to Auvik or NetBox?",
        a: "Auvik is a real-time monitoring and topology platform — we don't compete there. NetBox is a powerful source-of-truth IPAM, but heavy to self-host and expensive in cloud form. We sit between them: lightweight IPAM you can use in 60 seconds, plus a multi-vendor firewall configurator that neither Auvik nor NetBox offers. See the comparison table above.",
    },
    {
        q: "What's your data residency policy?",
        a: "Data is hosted on US infrastructure. We are GDPR-aligned for EU users via Standard Contractual Clauses and CCPA-compliant for California users. You can export or delete your data anytime by emailing info@cybersecurityforefront.com or from account settings.",
    },
];

// JSON-LD: SoftwareApplication + FAQPage (built from FAQ_ITEMS so copy stays in sync)
const PRODUCT_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "@id": `${SITE_DOMAIN}/#software`,
    name: "Cybersecurity Forefront",
    applicationCategory: "SecurityApplication",
    operatingSystem: "Web",
    url: `${SITE_DOMAIN}/`,
    description:
        "Chat-first firewall configurator (Fortinet, WatchGuard, Zyxel, pfSense), multi-tenant Client IPAM, and free compliance tools for MSPs and security engineers.",
    publisher: { "@id": `${SITE_DOMAIN}/#organization` },
    offers: [
        { "@type": "Offer", name: "Free", price: "0", priceCurrency: "USD", url: `${SITE_DOMAIN}/pricing`, availability: "https://schema.org/InStock" },
        { "@type": "Offer", name: "Pro", price: "19.90", priceCurrency: "USD", url: `${SITE_DOMAIN}/pricing`, availability: "https://schema.org/InStock" },
        { "@type": "Offer", name: "MSP", price: "199", priceCurrency: "USD", url: `${SITE_DOMAIN}/pricing`, availability: "https://schema.org/InStock" },
    ],
};
const FAQ_SCHEMA = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "@id": `${SITE_DOMAIN}/#faq`,
    mainEntity: FAQ_ITEMS.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
};

// =============================================================================
// Demo strip — 3 pre-scripted prompts that animate a canned result inline.
// =============================================================================

const DEMO_PROMPTS = [
    {
        id: "subnet",
        icon: Network,
        prompt: "Calculate subnet 10.10.0.0/24",
        toolLink: "/tools/subnet-calculator",
        toolLabel: "Try with your own data → Subnet Calculator",
        payload: {
            cidr: "10.10.0.0/24",
            network: "10.10.0.0",
            broadcast: "10.10.0.255",
            netmask: "255.255.255.0",
            usable: "10.10.0.1 – 10.10.0.254",
            hosts: 254,
            gateway: "10.10.0.1",
        },
        render: (p) => (
            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 font-mono text-[12.5px]">
                {[
                    ["CIDR", p.cidr],
                    ["Network", p.network],
                    ["Broadcast", p.broadcast],
                    ["Netmask", p.netmask],
                    ["Usable", p.usable],
                    ["Hosts", String(p.hosts)],
                    ["Suggested gw", p.gateway],
                ].map(([k, v]) => (
                    <React.Fragment key={k}>
                        <dt className="text-text-dim">{k}</dt>
                        <dd className="text-accent-cyan">{v}</dd>
                    </React.Fragment>
                ))}
            </dl>
        ),
    },
    {
        id: "dns",
        icon: Globe,
        prompt: "Check DNS for example.com",
        toolLink: "/tools/dns-health",
        toolLabel: "Try with your own data → DNS Health",
        payload: {
            a: "93.184.216.34",
            aaaa: "2606:2800:220:1:248:1893:25c8:1946",
            mx: "Not configured",
            spf: "v=spf1 -all",
            dmarc: "v=DMARC1; p=reject;",
            warning: "No MX records — domain cannot receive email",
        },
        render: (p) => (
            <>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 font-mono text-[12.5px]">
                    {[
                        ["A", p.a],
                        ["AAAA", p.aaaa],
                        ["MX", p.mx],
                        ["SPF", p.spf],
                        ["DMARC", p.dmarc],
                    ].map(([k, v]) => (
                        <React.Fragment key={k}>
                            <dt className="text-text-dim">{k}</dt>
                            <dd className={v === "Not configured" ? "text-accent-amber break-all" : "text-accent-cyan break-all"}>
                                {v}
                            </dd>
                        </React.Fragment>
                    ))}
                </dl>
                <p className="mt-3 inline-flex items-center gap-1.5 text-[12px] px-2 py-1 rounded bg-accent-amber/10 border border-accent-amber/30 text-accent-amber">
                    <ShieldAlert className="w-3 h-3" />
                    {p.warning}
                </p>
            </>
        ),
    },
    {
        id: "cve",
        icon: ShieldAlert,
        prompt: "Is CVE-2024-21733 critical?",
        toolLink: "/tools/cve-lookup",
        toolLabel: "Try with your own data → CVE Lookup",
        payload: {
            id: "CVE-2024-21733",
            cvss: 5.3,
            severity: "Medium",
            product: "Apache Tomcat",
            affected: "9.0.0-M1 – 9.0.83",
            patched: "9.0.85+",
            recommendation: "Patch immediately if internet-facing",
        },
        render: (p) => (
            <>
                <div className="flex items-center gap-2 mb-3">
                    <span className="font-mono text-[12.5px] text-accent-cyan">{p.id}</span>
                    <span className="px-2 py-0.5 rounded text-[11px] font-mono uppercase tracking-wider bg-accent-amber/10 border border-accent-amber/40 text-accent-amber">
                        {p.severity} · CVSS {p.cvss}
                    </span>
                </div>
                <dl className="grid grid-cols-[max-content_1fr] gap-x-4 gap-y-1.5 font-mono text-[12.5px]">
                    {[
                        ["Product", p.product],
                        ["Affected", p.affected],
                        ["Patched", p.patched],
                    ].map(([k, v]) => (
                        <React.Fragment key={k}>
                            <dt className="text-text-dim">{k}</dt>
                            <dd className="text-text-primary">{v}</dd>
                        </React.Fragment>
                    ))}
                </dl>
                <p className="mt-3 text-[12.5px] text-accent-mint">→ {p.recommendation}</p>
            </>
        ),
    },
];

const DemoStrip = ({ onSendToChat }) => {
    const trigger = (item) => {
        // Live mode: hand the prompt straight to the real chat instead of
        // rendering a canned card. The chat handles thinking → tool call →
        // tool result → streamed reply via SSE.
        onSendToChat?.(item.prompt);
    };

    return (
        <div className="grid gap-4 sm:grid-cols-3" data-testid="demo-strip">
            {DEMO_PROMPTS.map((item) => {
                const Icon = item.icon;
                return (
                    <button
                        key={item.id}
                        type="button"
                        onClick={() => trigger(item)}
                        className="text-left rounded-xl border border-white/[0.06] hover:border-accent-cyan/40 bg-surface/40 backdrop-blur-md p-5 transition-[border-color,box-shadow] duration-200"
                        data-testid={`demo-chip-${item.id}`}
                    >
                        <div className="flex items-center gap-2.5 mb-3">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                <Icon className="w-4 h-4" />
                            </span>
                            <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-dim">
                                Live demo
                            </span>
                        </div>
                        <p className="font-mono text-[13px] text-text-primary leading-relaxed">
                            “{item.prompt}”
                        </p>
                        <p className="mt-3 text-[11.5px] text-text-dim inline-flex items-center gap-1">
                            <ArrowRight className="w-3 h-3" />
                            Run in the chat above
                        </p>
                    </button>
                );
            })}
        </div>
    );
};

// =============================================================================
// Section components
// =============================================================================

const AnswerBlock = ({ children, testid }) => (
    <p
        className="mt-3 text-[14px] text-text-muted leading-relaxed max-w-3xl"
        data-testid={testid || "answer-block"}
    >
        {children}
    </p>
);

const FeaturePreview = ({ side, eyebrow, title, body, bullets, ctaLabel, ctaTo, image, alt, testid }) => (
    <div
        className={`grid gap-10 lg:gap-14 items-center ${side === "right" ? "lg:grid-cols-[1fr_1.1fr]" : "lg:grid-cols-[1.1fr_1fr]"}`}
        data-testid={testid}
    >
        <div className={side === "right" ? "lg:order-2" : ""}>
            <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                {eyebrow}
            </p>
            <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                {title}
            </h2>
            <AnswerBlock>{body}</AnswerBlock>
            <ul className="mt-5 space-y-2 text-[13.5px] text-text-muted">
                {bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2">
                        <Check className="w-3.5 h-3.5 text-accent-mint mt-1 shrink-0" />
                        <span>{b}</span>
                    </li>
                ))}
            </ul>
            <Link
                to={ctaTo}
                className="mt-7 inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-accent-cyan text-canvas font-medium text-[13px] hover:bg-accent-cyan/90 transition-colors"
            >
                {ctaLabel}
                <ArrowRight className="w-3.5 h-3.5" />
            </Link>
        </div>
        <div className={side === "right" ? "lg:order-1" : ""}>
            <div className="relative rounded-2xl border border-white/[0.06] bg-surface/40 backdrop-blur-md overflow-hidden shadow-heavy-popover">
                <img
                    src={image}
                    alt={alt}
                    loading="lazy"
                    decoding="async"
                    className="block w-full h-auto"
                />
            </div>
        </div>
    </div>
);

const RoadmapItem = ({ item }) => {
    const styles = {
        live: "bg-accent-mint/10 border-accent-mint/40 text-accent-mint",
        active: "bg-accent-cyan/10 border-accent-cyan/40 text-accent-cyan",
        planned: "bg-white/[0.04] border-white/[0.1] text-text-muted",
    };
    const icons = { live: "✅", active: "🔄", planned: "📅" };
    return (
        <li className="flex items-start gap-3 rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-4">
            <span className={`shrink-0 inline-flex items-center gap-1.5 px-2 py-0.5 rounded text-[10.5px] font-mono uppercase tracking-[0.18em] border ${styles[item.state]}`}>
                <span aria-hidden>{icons[item.state]}</span>
                {item.label}
            </span>
            <span className="text-[13.5px] text-text-primary leading-relaxed">{item.text}</span>
        </li>
    );
};

// =============================================================================
// Page
// =============================================================================

export const Home = () => {
    const { company } = useOutletContext() || {};
    const location = useLocation();
    const [initialMessage, setInitialMessage] = useState(null);
    const chatAnchorRef = useRef(null);
    const chatRef = useRef(null);

    useEffect(() => {
        const msg = location.state?.chatMessage;
        if (msg) setInitialMessage(msg);
    }, [location.state]);

    const scrollToChat = () => {
        chatAnchorRef.current?.scrollIntoView({
            behavior: "smooth",
            block: "center",
        });
    };

    /**
     * Live demo-strip handoff: forward a chip prompt to the actual chat
     * component instead of rendering a canned card. Phase D.1 makes the 3
     * homepage demo prompts trigger real function calls.
     */
    const sendToChat = (text) => {
        scrollToChat();
        // Defer past the smooth-scroll's first frame so the chat is in view
        // when the user message bubble appears.
        requestAnimationFrame(() => {
            chatRef.current?.sendMessage?.(text);
        });
    };

    const isAdmin = false; // public homepage rendering; banner targets non-admins
    const showPaidWaitlist = WAITLIST_MODE && !isAdmin;

    return (
        <>
            <SEO
                title="Your Network Operations Co-Pilot"
                description="Multi-vendor firewall configs, lightweight IPAM, and free network tools — all in plain English. Built for SMB IT, freelance network engineers, and growing teams."
                canonicalPath="/"
                schema={[PRODUCT_SCHEMA, FAQ_SCHEMA]}
            />

            <div className="pt-20 md:pt-24 pb-6" data-testid="home-page">
                {/* ============================================================
                    Section 1 — HERO 2-col (60/40 desktop, stacked mobile)
                ============================================================ */}
                <section
                    id="section-1"
                    className="relative isolate"
                    data-testid="hero-section"
                >
                    <SpotlightCursor />
                    <div
                        aria-hidden
                        className="pointer-events-none absolute inset-0 bg-grid bg-grid-mask opacity-60"
                    />
                    <div
                        aria-hidden
                        className="pointer-events-none absolute left-1/2 top-32 -translate-x-1/2 h-[380px] w-[680px] rounded-full blur-[120px]"
                        style={{
                            background:
                                "radial-gradient(closest-side, rgba(3,216,243,0.16), transparent 70%)",
                        }}
                    />

                    <div className="relative mx-auto max-w-7xl px-5 md:px-10 py-12 md:py-16">
                        <div className="grid gap-10 lg:grid-cols-[1.5fr_1fr] items-center">
                            {/* Left 60% */}
                            <div>
                                <motion.div
                                    initial={{ opacity: 0, y: -4 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.5 }}
                                    className="inline-flex items-center gap-2 px-3 py-1 mb-6 rounded-full border border-white/[0.07] bg-surface/60 backdrop-blur-sm"
                                    data-testid="hero-pill"
                                >
                                    <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-accent-mint">
                                        <span className="absolute inset-0 rounded-full bg-accent-mint animate-pulse-cyan" />
                                    </span>
                                    <span className="text-[10.5px] font-mono uppercase tracking-[0.22em] text-text-muted">
                                        Live — concierge · IPAM · ZeroConf
                                    </span>
                                </motion.div>

                                <motion.h1
                                    initial={{ opacity: 0, y: 8 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.05 }}
                                    className="font-display font-semibold text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.05] text-text-primary"
                                    data-testid="hero-title"
                                >
                                    Your Network Operations{" "}
                                    <span className="text-accent-cyan">Co-Pilot</span>.
                                </motion.h1>

                                <motion.p
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.18 }}
                                    className="mt-5 max-w-xl text-[15px] text-text-muted leading-relaxed"
                                    data-testid="hero-subtitle"
                                >
                                    Multi-vendor firewall configs, lightweight IPAM, free
                                    network tools — all in plain English. Built for SMB IT,
                                    freelance network engineers, and growing teams.
                                </motion.p>

                                <motion.div
                                    initial={{ opacity: 0, y: 6 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ duration: 0.6, delay: 0.28 }}
                                    className="mt-7 flex flex-col sm:flex-row gap-3"
                                >
                                    <button
                                        type="button"
                                        onClick={scrollToChat}
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-accent-cyan text-canvas font-medium text-[14px] hover:bg-accent-cyan/90 hover:shadow-glow-cyan transition-all"
                                        data-testid="hero-cta-chat"
                                    >
                                        Try the AI co-pilot
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </button>
                                    <Link
                                        to="/tools"
                                        className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-white/10 text-text-primary font-medium text-[14px] hover:border-white/20 transition-colors"
                                        data-testid="hero-cta-tools"
                                    >
                                        Browse free tools
                                    </Link>
                                </motion.div>

                                <ul
                                    className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[12px] text-text-muted"
                                    data-testid="hero-trust-strip"
                                >
                                    {[
                                        "No demo calls",
                                        "Cancel anytime",
                                        "7 free tools, no signup",
                                    ].map((t) => (
                                        <li
                                            key={t}
                                            className="inline-flex items-center gap-1.5"
                                        >
                                            <Check className="w-3 h-3 text-accent-mint" />
                                            {t}
                                        </li>
                                    ))}
                                </ul>
                            </div>

                            {/* Right 40% — chat container ridotto */}
                            <div ref={chatAnchorRef} className="w-full">
                                <div
                                    className="max-w-md mx-auto lg:mx-0 lg:ml-auto"
                                    data-testid="hero-chat-wrapper"
                                >
                                    <Chat
                                        ref={chatRef}
                                        initialMessage={initialMessage}
                                        compact
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* ============================================================
                    Section 2 — DEMO STRIP (3 pre-scripted prompts)
                ============================================================ */}
                <section
                    id="section-2"
                    className="mt-20 md:mt-28 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="demo-section"
                >
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            See it in action
                        </p>
                        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                            Ask in plain English. Get answers in seconds.
                        </h2>
                        <AnswerBlock>
                            Pick any of these pre-scripted prompts and watch the co-pilot
                            answer in a structured card. Each result links to the same tool
                            you can use with your own data — no signup, no email gate.
                        </AnswerBlock>
                    </Reveal>
                    <Reveal>
                        <div className="mt-8">
                            <DemoStrip onSendToChat={sendToChat} />
                        </div>
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 3 — THREE PILLARS
                ============================================================ */}
                <section
                    id="section-3"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="pillars-section"
                >
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            Capabilities
                        </p>
                        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                            Three pillars, one platform
                        </h2>
                        <AnswerBlock>
                            Cybersecurity Forefront is built around three product pillars:
                            free browser tools for daily network ops, a multi-vendor
                            firewall configurator that ships production CLI/XML, and a
                            lightweight IPAM that tracks clients, subnets, and hardware.
                        </AnswerBlock>
                    </Reveal>
                    <Reveal stagger className="mt-10 grid gap-5 md:grid-cols-3">
                        {PILLARS.map((p) => {
                            const Icon = p.icon;
                            return (
                                <RevealItem
                                    key={p.title}
                                    className="rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-6 flex flex-col"
                                >
                                    <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg bg-canvas border border-accent-cyan/30 text-accent-cyan">
                                        <Icon className="w-5 h-5" />
                                    </div>
                                    <h3 className="mt-4 text-lg font-semibold text-text-primary">
                                        {p.title}
                                    </h3>
                                    <p className="mt-2 text-[13.5px] text-text-muted leading-relaxed flex-1">
                                        {p.body}
                                    </p>
                                    <Link
                                        to={p.cta.to}
                                        className="mt-5 inline-flex items-center gap-1.5 text-[13px] font-medium text-accent-cyan hover:underline"
                                        data-testid={`pillar-cta-${p.title.toLowerCase().replace(/\W+/g, "-")}`}
                                    >
                                        {p.cta.label}
                                        <ArrowRight className="w-3.5 h-3.5" />
                                    </Link>
                                </RevealItem>
                            );
                        })}
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 4 — FREE TOOLS GRID (7 cards)
                ============================================================ */}
                <section
                    id="section-4"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="tools-section"
                >
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            Free Tools
                        </p>
                        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                            Seven tools, free forever. No signup, no ads.
                        </h2>
                        <p className="mt-2 text-[14px] text-text-muted">
                            Bookmark these. We don&apos;t gate utilities behind email forms.
                        </p>
                        <AnswerBlock>
                            Cybersecurity Forefront offers 7 browser-based network tools:
                            subnet calculator, DNS health check, CVE lookup, TLS inspector,
                            security headers analyzer, password breach check, and NIS2
                            compliance quiz. No signup, no email, no ads — accessible at
                            /tools.
                        </AnswerBlock>
                    </Reveal>
                    <Reveal stagger className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
                        {FREE_TOOLS.map((t) => {
                            const Icon = t.icon;
                            const slug = t.to.split("/").pop();
                            return (
                                <RevealItem key={t.to}>
                                    <Link
                                        to={t.to}
                                        className="group block h-full rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-5 hover:border-accent-cyan/40 transition-colors"
                                        data-testid={`tool-card-${slug}`}
                                    >
                                        <div className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-canvas border border-white/10 text-accent-cyan group-hover:border-accent-cyan/40 transition-colors">
                                            <Icon className="w-4 h-4" />
                                        </div>
                                        <h3 className="mt-4 text-[15px] font-semibold text-text-primary">
                                            {t.title}
                                        </h3>
                                        <p className="mt-2 text-[12.5px] text-text-muted leading-relaxed">
                                            {t.body}
                                        </p>
                                        <p className="mt-3 italic text-[11px] text-text-dim leading-relaxed">
                                            {t.hint}
                                        </p>
                                        <span className="mt-3 inline-flex items-center gap-1 text-[11px] font-mono uppercase tracking-[0.18em] text-text-dim group-hover:text-accent-cyan transition-colors">
                                            Use it
                                            <ArrowRight className="w-3 h-3" />
                                        </span>
                                    </Link>
                                </RevealItem>
                            );
                        })}
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 5 — ZEROCONF PREVIEW (text left, screenshot right)
                ============================================================ */}
                <section
                    id="section-5"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="zeroconf-preview-section"
                >
                    <Reveal>
                        <FeaturePreview
                            side="left"
                            testid="zeroconf-preview"
                            eyebrow="ZeroConf · Multi-vendor"
                            title="Stop translating vendor docs at 2 AM"
                            body="The ZeroConf wizard handles every firewall vendor differently — Fortinet CLI dialect, WatchGuard syntax quirks, Zyxel command structure, pfSense XML import — so you stop context-switching between 5 vendor manuals."
                            bullets={[
                                "Fortinet FortiGate — CLI + XML import",
                                "WatchGuard Firebox — full CLI generation",
                                "Zyxel USG / ATP series — CLI",
                                "Netgate pfSense — XML import",
                                "Generic CLI fallback for unsupported vendors",
                                "37 hardware models pre-cataloged",
                            ]}
                            ctaLabel="Browse supported models"
                            ctaTo="/zeroconf"
                            image="/screenshots/configurator-preview.png"
                            alt="ZeroConf multi-vendor firewall configurator wizard interface — dark theme dialog with Fortinet, WatchGuard, Zyxel, and pfSense vendor selection."
                        />
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 6 — IPAM PREVIEW (screenshot left, text right)
                ============================================================ */}
                <section
                    id="section-6"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="ipam-preview-section"
                >
                    <Reveal>
                        <FeaturePreview
                            side="right"
                            testid="ipam-preview"
                            eyebrow="Client Data Suite · IPAM"
                            title="Subnet maps without spreadsheets"
                            body="Track every client, every subnet, every piece of hardware, every critical IP — in one workspace. CIDR overlap detection prevents production headaches before they happen. CPE-matched hardware inventory is the foundation for automated CVE alerts (coming Q1 2026)."
                            bullets={[
                                "Clients with NIS2 scope tracking",
                                "Subnets with VLAN, gateway, DNS, purpose tagging",
                                "Hardware inventory across 43+ vendor models with auto-populated CPE",
                                "Important IPs (DCs, DNS, gateways, NAS) with criticality levels",
                                "Global search Spotlight-style (⌘K)",
                                "CSV export per client · Multi-tenant by design",
                            ]}
                            ctaLabel="See the suite"
                            ctaTo="/ipam"
                            image="/screenshots/ipam-preview.png"
                            alt="Client Data Suite (IPAM) dashboard — dark theme list of clients with subnet count, hardware count, NIS2 scope, and last-updated timestamps."
                        />
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 7 — AI CO-PILOT FLOW
                ============================================================ */}
                <section
                    id="section-7"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="copilot-section"
                >
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            AI co-pilot
                        </p>
                        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                            Your AI co-pilot, in 3 steps
                        </h2>
                        <AnswerBlock>
                            The co-pilot translates plain-English questions into structured
                            tool calls — subnet maths, DNS lookups, CVE matching against
                            your hardware inventory — and returns formatted answers inline
                            in the chat. Live now. Click any prompt above to see the co-pilot in action.
                        </AnswerBlock>
                    </Reveal>
                    <Reveal stagger className="mt-10 grid gap-4 md:grid-cols-3">
                        {[
                            {
                                num: "1",
                                icon: MessageSquare,
                                title: "You ask in plain English",
                                example: "“Calculate /24 to /28 split for branch network”",
                            },
                            {
                                num: "2",
                                icon: Wand2,
                                title: "AI picks the right tool",
                                example: "Function call: subnet_calculator(cidr=\"10.0.0.0/24\", split_to=28)",
                            },
                            {
                                num: "3",
                                icon: Check,
                                title: "Result back in chat",
                                example: "Card with 16 /28 subnets, gateways, host ranges, and a save-to-IPAM button.",
                            },
                        ].map((s) => {
                            const Icon = s.icon;
                            return (
                                <RevealItem
                                    key={s.num}
                                    className="rounded-xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-5"
                                >
                                    <div className="flex items-center gap-2 mb-3">
                                        <span className="inline-flex h-7 w-7 items-center justify-center rounded-md bg-accent-cyan/10 border border-accent-cyan/30 text-accent-cyan font-mono text-[12px]">
                                            {s.num}
                                        </span>
                                        <Icon className="w-4 h-4 text-text-dim" />
                                    </div>
                                    <h3 className="text-[15px] font-semibold text-text-primary">
                                        {s.title}
                                    </h3>
                                    <p className="mt-2 font-mono text-[12px] text-text-muted leading-relaxed">
                                        {s.example}
                                    </p>
                                </RevealItem>
                            );
                        })}
                    </Reveal>
                    <Reveal>
                        <div className="mt-10 grid gap-3 md:grid-cols-2 max-w-4xl">
                            {[
                                {
                                    title: "“Translate ACL from Cisco to pfSense”",
                                    note: "Multi-vendor config translation (Q1 2026)",
                                },
                                {
                                    title: "“What CVEs affect my FortiGate 60F?”",
                                    note: "IPAM hardware lookup + NVD CVE match",
                                },
                                {
                                    title: "“Show me clients without DMARC configured”",
                                    note: "DNS check across all your clients in IPAM",
                                },
                                {
                                    title: "“Build SPF record for my domain”",
                                    note: "SPF validator + generator",
                                },
                            ].map((ex) => (
                                <div
                                    key={ex.title}
                                    className="rounded-lg border border-white/[0.05] bg-surface/30 p-4"
                                >
                                    <p className="font-mono text-[13px] text-text-primary">
                                        {ex.title}
                                    </p>
                                    <p className="mt-1 text-[11.5px] text-text-dim">
                                        → {ex.note}
                                    </p>
                                </div>
                            ))}
                        </div>
                        <p className="mt-5 text-[11.5px] text-text-dim italic max-w-2xl">
                            Live now. Try the demo prompts above — the co-pilot actually invokes the tools and returns formatted results in the chat.
                        </p>
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 8 — COMPARISON TABLE (native HTML for GEO)
                ============================================================ */}
                <section
                    id="section-8"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="comparison-section"
                >
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            Comparison
                        </p>
                        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                            How we compare
                        </h2>
                        <AnswerBlock>
                            Cybersecurity Forefront sits between heavy enterprise IPAM
                            (NetBox, $7,500/yr for SSO) and real-time monitoring suites
                            (Auvik, OpManager). We focus on the workflow before and after
                            monitoring: configuring devices and documenting infrastructure,
                            with transparent pricing and self-serve signup.
                        </AnswerBlock>
                    </Reveal>
                    <Reveal>
                        <div className="mt-8 overflow-x-auto rounded-xl border border-white/[0.06] bg-surface/40">
                            <table
                                className="min-w-full text-[13px] text-text-muted"
                                data-testid="comparison-table"
                            >
                                <thead className="bg-canvas/60 border-b border-white/[0.06]">
                                    <tr>
                                        {COMPARISON.headers.map((h, i) => (
                                            <th
                                                key={h}
                                                className={`px-4 py-3 text-left font-mono text-[11px] uppercase tracking-[0.18em] ${
                                                    i === 1 ? "text-accent-cyan" : "text-text-dim"
                                                }`}
                                            >
                                                {h}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {COMPARISON.rows.map((row, i) => (
                                        <tr
                                            key={i}
                                            className="border-b border-white/[0.04] last:border-b-0"
                                        >
                                            {row.map((cell, j) => (
                                                <td
                                                    key={j}
                                                    className={`px-4 py-3 align-top ${
                                                        j === 0
                                                            ? "font-medium text-text-primary"
                                                            : j === 1
                                                                ? "text-text-primary"
                                                                : ""
                                                    }`}
                                                >
                                                    {cell}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                        <p className="mt-5 text-[13px] text-text-muted leading-relaxed max-w-3xl">
                            We don&apos;t do real-time SNMP monitoring or full-fleet network
                            observability — Auvik and OpManager do that better. We focus on
                            the workflow before and after: configuring devices and
                            documenting infrastructure.
                        </p>
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 9 — VALUES (6-col chips)
                ============================================================ */}
                <section
                    id="section-9"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="values-section"
                >
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            Values
                        </p>
                        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                            Built for the practitioner, not the procurement department
                        </h2>
                    </Reveal>
                    <Reveal stagger className="mt-8 grid gap-3 grid-cols-2 sm:grid-cols-3 lg:grid-cols-6">
                        {VALUES.map((v) => (
                            <RevealItem
                                key={v}
                                className="rounded-lg border border-white/[0.06] bg-surface/40 backdrop-blur-md p-4"
                            >
                                <Check className="w-4 h-4 text-accent-mint" />
                                <p className="mt-2 text-[12.5px] text-text-primary leading-snug">
                                    {v}
                                </p>
                            </RevealItem>
                        ))}
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 10 — ROADMAP
                ============================================================ */}
                <section
                    id="section-10"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="roadmap-section"
                >
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            Roadmap
                        </p>
                        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                            We ship publicly
                        </h2>
                        <p className="mt-2 text-[14px] text-text-muted">
                            Real timeline. No vaporware.
                        </p>
                    </Reveal>
                    <Reveal>
                        <ul className="mt-8 grid gap-3 max-w-3xl">
                            {ROADMAP.map((r) => (
                                <RoadmapItem key={r.text} item={r} />
                            ))}
                        </ul>
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 11 — PRICING TEASER
                ============================================================ */}
                <section
                    id="section-11"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="pricing-teaser-section"
                >
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            Pricing
                        </p>
                        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                            Predictable pricing. No &ldquo;contact sales&rdquo;.
                        </h2>
                    </Reveal>
                    <Reveal stagger className="mt-10 grid gap-4 md:grid-cols-3">
                        {PRICING_TEASER.map((p) => (
                            <RevealItem
                                key={p.key}
                                className={`relative rounded-xl border ${
                                    p.popular
                                        ? "border-accent-cyan/40 bg-accent-cyan/[0.04]"
                                        : "border-white/[0.06] bg-surface/40"
                                } backdrop-blur-md p-6 flex flex-col`}
                            >
                                {p.popular && (
                                    <span className="absolute -top-2.5 left-6 inline-flex items-center gap-1 px-2 py-0.5 rounded-md bg-accent-cyan text-canvas text-[10px] font-mono uppercase tracking-[0.18em]">
                                        <Sparkles className="w-2.5 h-2.5" />
                                        Most popular
                                    </span>
                                )}
                                <h3 className="text-xl font-semibold text-text-primary">
                                    {p.name}
                                </h3>
                                <div className="mt-2 flex items-baseline gap-1">
                                    <span className="font-display font-semibold text-3xl text-text-primary tracking-tight">
                                        {p.price}
                                    </span>
                                    <span className="text-[12px] text-text-muted">
                                        {p.cadence}
                                    </span>
                                </div>
                                <ul className="mt-4 space-y-2 text-[13px] text-text-muted flex-1">
                                    {p.features.map((f) => (
                                        <li key={f} className="flex items-start gap-2">
                                            <Check className="w-3.5 h-3.5 text-accent-mint mt-0.5 shrink-0" />
                                            <span>{f}</span>
                                        </li>
                                    ))}
                                </ul>
                                {/* CTA — Pro tier respects WAITLIST_MODE */}
                                <div className="mt-5">
                                    {p.key === "pro_monthly" && showPaidWaitlist ? (
                                        <WaitlistButton
                                            source="homepage:pricing_teaser_pro"
                                            variant="primary"
                                            dataTestId="pricing-teaser-waitlist-pro"
                                        >
                                            Get notified
                                        </WaitlistButton>
                                    ) : (
                                        <Link
                                            to={p.cta.to}
                                            className={`block text-center px-4 py-2.5 rounded-md font-medium text-[13px] transition-colors ${
                                                p.cta.variant === "primary"
                                                    ? "bg-accent-cyan text-canvas hover:bg-accent-cyan/90"
                                                    : "border border-white/10 text-text-primary hover:border-white/20"
                                            }`}
                                            data-testid={`pricing-teaser-cta-${p.key}`}
                                        >
                                            {p.cta.label}
                                        </Link>
                                    )}
                                </div>
                            </RevealItem>
                        ))}
                    </Reveal>
                    <Reveal>
                        <div className="mt-6 text-center">
                            <Link
                                to="/pricing"
                                className="text-[13px] text-accent-cyan hover:underline inline-flex items-center gap-1"
                            >
                                See full pricing details
                                <ArrowRight className="w-3 h-3" />
                            </Link>
                        </div>
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 12 — FAQ (15 Q&A)
                ============================================================ */}
                <section
                    id="section-12"
                    className="mt-24 md:mt-32 mx-auto max-w-3xl px-5 md:px-10"
                    data-testid="faq-section"
                >
                    <Reveal>
                        <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                            FAQ
                        </p>
                        <h2 className="mt-3 font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight">
                            Frequently asked questions
                        </h2>
                    </Reveal>
                    <Reveal>
                        <Accordion
                            type="single"
                            collapsible
                            className="mt-8 w-full"
                            data-testid="home-faq-accordion"
                        >
                            {FAQ_ITEMS.map((f, i) => (
                                <AccordionItem
                                    key={f.q}
                                    value={`faq-${i}`}
                                    className="border-white/[0.06]"
                                >
                                    <AccordionTrigger
                                        className="text-left text-[15px] font-medium text-text-primary hover:text-accent-cyan hover:no-underline"
                                        data-testid={`home-faq-q-${i}`}
                                    >
                                        {f.q}
                                    </AccordionTrigger>
                                    <AccordionContent
                                        className="text-[14px] text-text-muted leading-relaxed"
                                        data-testid={`home-faq-a-${i}`}
                                    >
                                        {f.a}
                                    </AccordionContent>
                                </AccordionItem>
                            ))}
                        </Accordion>
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 13 — NEWSLETTER CTA
                ============================================================ */}
                <section
                    id="section-13"
                    className="mt-24 md:mt-32 mx-auto max-w-3xl px-5 md:px-10"
                    data-testid="newsletter-section"
                >
                    <Reveal>
                        <div className="rounded-2xl border border-white/[0.06] bg-surface/40 backdrop-blur-md p-8 md:p-10">
                            <div className="flex items-center gap-2 mb-3">
                                <Mail className="w-4 h-4 text-accent-cyan" />
                                <p className="text-[10.5px] font-mono uppercase tracking-[0.32em] text-accent-cyan">
                                    Stay ahead of CVEs
                                </p>
                            </div>
                            <h2 className="font-display font-semibold text-2xl sm:text-3xl tracking-tight">
                                Weekly digest of CVEs that matter to you
                            </h2>
                            <p className="mt-3 text-[14px] text-text-muted leading-relaxed max-w-2xl">
                                Get a weekly digest with new CVEs matching your hardware
                                inventory plus product updates. No spam, no third-party
                                sharing.
                            </p>
                            <div className="mt-5 max-w-xs">
                                <WaitlistButton
                                    source="newsletter"
                                    variant="primary"
                                    dataTestId="newsletter-subscribe"
                                >
                                    Subscribe
                                </WaitlistButton>
                            </div>
                        </div>
                    </Reveal>
                </section>

                {/* ============================================================
                    Section 14 — FINAL CTA
                ============================================================ */}
                <section
                    id="section-14"
                    className="mt-24 md:mt-32 mx-auto max-w-7xl px-5 md:px-10"
                    data-testid="final-cta-section"
                >
                    <Reveal>
                        <div
                            className="rounded-2xl border border-accent-cyan/30 bg-accent-cyan/[0.04] p-10 md:p-14 text-center shadow-glow-cyan"
                        >
                            <Rocket className="w-7 h-7 text-accent-cyan mx-auto mb-4" />
                            <h2 className="font-display font-semibold text-3xl sm:text-4xl tracking-tight leading-tight max-w-3xl mx-auto">
                                Start with free tools, or jump to the co-pilot.
                            </h2>
                            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                                <Link
                                    to="/tools"
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md bg-accent-cyan text-canvas font-medium text-[14px] hover:bg-accent-cyan/90 transition-colors"
                                    data-testid="final-cta-tools"
                                >
                                    Browse free tools
                                    <ArrowRight className="w-3.5 h-3.5" />
                                </Link>
                                <button
                                    type="button"
                                    onClick={scrollToChat}
                                    className="inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-md border border-white/10 text-text-primary font-medium text-[14px] hover:border-white/20 transition-colors"
                                    data-testid="final-cta-chat"
                                >
                                    Talk to the co-pilot
                                    <ArrowUp className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    </Reveal>
                </section>

                {/* Section 15 — Footer is rendered by Layout (intact). */}
            </div>
        </>
    );
};

export default Home;
